import React from "react";
import { copyText } from "@/lib/clipboard";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth, hasPerm } from "@/context/AuthContext";
import { PageHeader } from "@/components/kit/PageHeader";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fmtNumber } from "@/lib/format";
import { fmtDate } from "@/lib/format";
import { useOptions } from "@/hooks/useOptions";
import { userOptions } from "@/lib/users";
import { MultiLinkDrawer } from "@/drawers/MultiLinkDrawer";

function IconBtn({ onClick, title, danger, testId, children }) {
  return (
    <button type="button" title={title} data-testid={testId}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${danger ? "text-muted-foreground hover:bg-[#EF4444]/10 hover:text-[#EF4444]" : "text-muted-foreground hover:bg-white/[0.06] hover:text-foreground"}`}>
      {children}
    </button>
  );
}

function StatusBadge({ row }) {
  const active = row.status === "active";
  const cls = active ? "bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30" : "bg-[#EAB308]/15 text-[#EAB308] border-[#EAB308]/30";
  return <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${cls}`} data-testid={`multilink-status-${row.id}`}>{active ? "Active" : "Disabled"}</span>;
}

export default function MultiLinks() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [drawer, setDrawer] = React.useState({ open: false, item: null });
  const [del, setDel] = React.useState({ open: false, row: null });
  const isBuyer = user.role === "buyer";
  const opts = useOptions();

  const { data, isLoading } = useQuery({
    queryKey: ["multilinks"],
    queryFn: async () => (await http.get("/multilinks")).data,
  });
  const rows = data?.items || [];

  const remove = async (id) => {
    try {
      await http.delete(`/multilinks/${id}`);
      qc.invalidateQueries({ queryKey: ["multilinks"] });
      toast.success("Multi Link deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const copy = (text, msg) => copyText(text, msg);
  const shortLink = (link) => (link || "").replace(/^https?:\/\//, "");

  const columns = [
    { key: "tenant_seq", label: "ID", sortable: true, mono: true, sortValue: (r) => Number(r.tenant_seq || 0),
      render: (r) => <span className="font-mono text-xs text-muted-foreground">#{r.tenant_seq}</span> },
    { key: "name", label: "Name", sortable: true, render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    ...(!isBuyer ? [{ key: "buyer_name", label: "Buyer", sortable: true, sortValue: (r) => (r.buyer_name || "").toLowerCase(), render: (r) => (
      r.buyer_id ? (
        <div className="min-w-0" data-testid={`multilink-buyer-${r.id}`}>
          <div className="truncate text-sm text-foreground">{r.buyer_seq != null ? `#${r.buyer_seq} ` : ""}{r.buyer_name || "—"}</div>
          {r.buyer_email && <div className="truncate text-[11px] text-muted-foreground">{r.buyer_email}</div>}
        </div>
      ) : <span className="text-xs text-muted-foreground">—</span>
    ) }] : []),
    { key: "stream_key", label: "Stream Key", render: (r) => (
      <button type="button" onClick={(e) => { e.stopPropagation(); copy(r.stream_key, "Stream key copied"); }}
        data-testid={`multilink-key-${r.id}`} title="Click to copy"
        className="inline-flex items-center gap-1.5 rounded-md bg-white/[0.04] px-2 py-1 font-mono text-xs text-[#A78BFA] transition-colors hover:bg-white/[0.08]">
        {r.stream_key || "—"}<Copy className="h-3 w-3 opacity-60" />
      </button>
    ) },
    { key: "tracking_domain", label: "Domain", render: (r) => <span className="text-xs text-muted-foreground">{r.tracking_domain || "—"}</span> },
    { key: "streams_count", label: "Streams", align: "right", mono: true, sortable: true, sortValue: (r) => Number(r.streams_count || 0),
      render: (r) => <span className="text-sm text-foreground" data-testid={`multilink-count-${r.id}`}>{r.active_streams_count}/{r.streams_count}</span> },
    { key: "status", label: "Status", sortable: true, sortValue: (r) => r.status, render: (r) => <StatusBadge row={r} /> },
    { key: "clicks", label: "Clicks", align: "right", mono: true, sortable: true, sortValue: (r) => Number(r.clicks || 0), render: (r) => fmtNumber(r.clicks) },
    { key: "created_at", label: "Created", sortable: true, sortValue: (r) => r.created_at, render: (r) => <span className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</span> },
    { key: "tracking_link", label: "Tracking Link", render: (r) => (
      <div className="flex items-center gap-1.5">
        <code className="max-w-[220px] truncate font-mono text-xs text-muted-foreground" title={r.tracking_link}>{shortLink(r.tracking_link)}</code>
        <IconBtn onClick={() => copy(r.tracking_link, "Tracking link copied")} title="Copy tracking link" testId={`multilink-copy-link-${r.id}`}><Copy className="h-3.5 w-3.5" /></IconBtn>
      </div>
    ) },
    { key: "_actions", label: "Actions", align: "right", render: (r) => (
      <div className="flex items-center justify-end gap-1">
        <IconBtn onClick={() => setDrawer({ open: true, item: r })} title="Edit" testId={`edit-multilink-${r.id}`}><Pencil className="h-4 w-4" /></IconBtn>
        {hasPerm(user, "streams.delete") &&
          <IconBtn onClick={() => setDel({ open: true, row: r })} title="Delete" danger testId={`delete-multilink-${r.id}`}><Trash2 className="h-4 w-4" /></IconBtn>}
      </div>
    ) },
  ];

  const filters = [
    ...(!isBuyer ? [{ key: "buyer_id", label: "Buyer", multi: true, getValue: (r) => r.buyer_id, options: userOptions(opts.users) }] : []),
    { key: "status", label: "Status", getValue: (r) => r.status,
      options: [{ value: "active", label: "Active" }, { value: "inactive", label: "Disabled" }] },
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Multi Links" subtitle="One tracking link that splits incoming traffic across several streams by weight." />
      <DataTablePro testId="multilinks-table" columns={columns} rows={rows} loading={isLoading}
        filters={filters} searchKeys={["tenant_seq", "_id_search", "name", "stream_key", "tracking_domain", "_buyer_search"]}
        searchPlaceholder="Search by ID, name, key, domain or buyer..." exportName="multilinks"
        onRowClick={(r) => setDrawer({ open: true, item: r })} emptyText="No multi links yet."
        toolbarRight={hasPerm(user, "streams.create") && (
          <Button onClick={() => setDrawer({ open: true, item: null })} data-testid="add-multilink-btn" className="h-9 gap-1.5 bg-white text-black hover:bg-white/90"><span className="text-base leading-none">+</span> Add Multi Link</Button>
        )} />
      <MultiLinkDrawer open={drawer.open} item={drawer.item} onOpenChange={(o) => setDrawer((d) => ({ ...d, open: o }))} />

      <AlertDialog open={del.open} onOpenChange={(o) => setDel((s) => ({ ...s, open: o }))}>
        <AlertDialogContent className="border-white/[0.1] bg-[#141417]" data-testid="multilink-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Multi Link?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <span className="font-medium text-foreground">{del.row?.name || "this multi link"}</span> and its aggregated stats. The underlying streams are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="multilink-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="multilink-delete-confirm" onClick={() => { remove(del.row.id); setDel({ open: false, row: null }); }} className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
